import { onMounted, onUnmounted, ref } from 'vue'
import { useGatForm } from '../gat-form/gat-form.vue'

export type FormFieldValidationRule<TValue> = (
  value: TValue
) => Promise<boolean | string | undefined> | boolean | string | undefined

export interface FormField<TValue> {
  id: string
  name: string
  aiExtractPropertyName?: string
  value: TValue
  validate: FormFieldValidationRule<TValue>
  reset?: () => void
  formName?: string
  isDirty?: boolean
  markDirty?: () => void
  setAiValue: (value: TValue, hint: string | undefined) => void
}

export interface UseFormFieldOptions<T> {
  id: string
  name: string
  value: T
  validate: FormFieldValidationRule<T>
  reset?: () => void
  setAiValue?: (value: T, hint: string | undefined) => void
  aiExtractPropertyName?: string
}

export function useFormField<T>(options: UseFormFieldOptions<T>) {
  const formContext = useGatForm()
  const isRegistered = ref(false)

  const isDirty = ref(false)

  function setAiValue(value: T, hint: string | undefined) {
    options.setAiValue?.(value, hint)
  }

  function onValidate(value: T) {
    isDirty.value = false
    return options.validate(value)
  }

  const field: FormField<T> = {
    id: options.id,
    name: options.name,
    validate: onValidate,
    value: options.value,
    reset: options.reset,
    formName: formContext.formName,
    aiExtractPropertyName: options.aiExtractPropertyName,
    isDirty: isDirty.value,
    setAiValue
  }

  onMounted(() => {
    formContext.registerField(field as FormField<unknown>)
    isRegistered.value = true
  })

  onUnmounted(() => {
    if (isRegistered.value) {
      formContext.unregisterField(options.id)
    }
  })

  const markDirty = () => {
    formContext.markFieldDirty(options.id)
    isDirty.value = true
  }

  return {
    formContext,
    isRegistered,
    readonly: formContext.readonly,
    formName: formContext.formName,
    markDirty
  }
}
